import { PropertyDetailsContext } from "./property-fields-context";
import { useState, useEffect, useCallback } from "react";
import { usePropertyFields } from "../../../../queries/use-property-fields";
import { transformApiPayloadToViewModels } from "../utils/transform-api-payload-to-view-models";
import { usePropertyFieldsForm } from "../forms/use-property-fields-form";

export type PropertyFieldsProviderProps = {
  children: React.ReactNode;
  propertyId: number;
};

export const PropertyFieldsProvider = ({ children, propertyId }: PropertyFieldsProviderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const { data: apiPayload, isLoading } = usePropertyFields(propertyId);

  const { sections, values } = transformApiPayloadToViewModels(apiPayload ?? []);

  const { form, handleSubmit, isSubmitting } = usePropertyFieldsForm({
    values,
    propertyId,
    onSuccess: () => setIsEditing(false),
  });

  const getValueOfField = useCallback(
    (dataKey: string) => {
      return values.find(v => v.data_key === dataKey)?.value;
    },
    [values]
  );

  // Initialize form values
  useEffect(() => {
    if (loaded || isLoading) return;

    const formValues: Record<string, any> = {};

    sections.forEach(section => {
      section.fields.forEach(field => {
        const value = getValueOfField(field.data_key);

        // Handle different field types
        switch (field.type) {
          case "date":
            formValues[field.data_key] = value ? value.split("T")[0] : "";
            break;
          case "number":
          case "currency":
            formValues[field.data_key] =
              value !== "" && value !== undefined ? Number(value) : undefined;
            break;
          case "boolean":
            formValues[field.data_key] =
              value !== "" && value !== undefined ? Boolean(value) : false;
            break;
          default:
            formValues[field.data_key] = value !== undefined ? String(value) : "";
        }
      });
    });

    // Set all form values at once
    form.reset(formValues);
    setLoaded(true);
  }, [sections, getValueOfField, form, loaded, values, isLoading]);

  if (!loaded) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  return (
    <PropertyDetailsContext.Provider
      value={{
        isEditing,
        setIsEditing,
        sections,
        getValueOfField,
        form,
        handleSubmit,
        isSubmitting,
      }}
    >
      {children}
    </PropertyDetailsContext.Provider>
  );
};
